/**
 * Main website navigation menu.
 * Utilizes 3rd party accessible menu solution.
 *
 * @link https://accessible-menu.dev/
 */
const NavMenu = (function() {
	const module = {
		/**
		 * Module options
		 */
		_opts: {
			selectors: {
				menu: '.navbar__menu',
				submenu: '.sub-menu',
				submenuToggle: '.sub-menu-toggle',
			}
		},
		/**
		 * Internal storage
		 */
		_data: {
			menuToggles: {},
		},
		/**
		 * The menu object
		 */
		_menu: {},
		/**
		 * Submenu observer for detecting changes
		 */
		_subMenuObserver: {},
		/**
		 * Main initialization function
		 */
		init: function() {
			// Setup menu
			this.setupMenu();
			// Setup submenu observer
			this.setupSubMenuObserver();
			// Setup menu item hover events
			this.setupMenuItemHoverEvents();
			// Build menu toggler
			this.buildMenuToggler(this._menu._elements);
			// Add menu item images
			$(`${this._opts.selectors.submenu} > li`).each((this.addMenuItemImages.bind(this)));
			// Add submenu headers
			$(`${this._opts.selectors.submenu}:not(.faux)`).each((this.addSubMenuHeaders.bind(this)));
			// Submenu header click
			$(document).on('click', '.sub-menu-header', this.handleSubMenuHeaderClick.bind(this));
			// Observe submenus
			$(`${this._opts.selectors.submenu} ${this._opts.selectors.submenu}`).each(this.observeSubMenus.bind(this));
		},
		/**
		 * Setup the Accessible Menu library.
		 *
		 * Using the TopLinkDisclosureMenu type so nav links can be links instead
		 * of only submenu toggles.
		 */
		setupMenu: function() {
			// @link https://accessible-menu.dev/top-link-disclosure-menus.html
			this._menu = new AccessibleMenu.TopLinkDisclosureMenu({
				menuElement: document.querySelector(this._opts.selectors.menu),
				controllerElement: document.querySelector('.navbar__toggler'),
				containerElement: document.querySelector('.navbar__nav'),
				submenuSubtoggleSelector: 'button',
				hoverType: 'on',
			})
		},
		/**
		 * Setup the submenu MutationObserver class
		 */
		setupSubMenuObserver: function() {
			const module = this;

			this._subMenuObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					const $submenu = $(mutation.target);
					if ($submenu.hasClass('show')) {
						module.setMenuImageAllowance($submenu);
					}
				});
			});
		},
		/**
		 * Recursive function for grabbing all submenu toggle elements out of
		 * our menu object. Handy for interacting with these and closing submenus
		 * programmatically.
		 *
		 * @param {object} elements The menu elements
		 */
		buildMenuToggler: function(elements) {
			const module = this;

			// Main/root menu
			if (elements.submenuToggles.length) {
				elements.submenuToggles.forEach((toggle, i) => {
					const key = $(toggle._dom.toggle).attr('id');
					module._data.menuToggles[key] = toggle
				})
			}
			// All other submenus
			elements.menuItems.forEach((val, i) => {
				if (val.isSubmenuItem) {
					// Grab the toggles
					val._elements.childMenu._elements.submenuToggles.forEach((toggle, i) => {
						const key = $(toggle._dom.toggle).attr('id');
						module._data.menuToggles[key] = toggle
					})
					// Keep going
					module.buildMenuToggler(val._elements.childMenu._elements);
				}
			});
		},
		/**
		 * Add menu item images as new list items.
		 *
		 * @param {integer} i
		 * @param {domElement}} menuItem
		 */
		addMenuItemImages: function(i, menuItem) {
			const $menuItem = $(menuItem);
			const menuItemLink = $menuItem.find('>a').attr('href');
			const $childMenu = $menuItem.find(this._opts.selectors.submenu);
			const listItemMarkup = `<li class="menu-item menu-item--image"><a href="${menuItemLink}"><span class="visually-hidden">View All</span></a></li>`;

			// If a list item has submenus, add image as new list item at end
			if ($childMenu.length) {
				$childMenu.append(listItemMarkup);
			// Otherwise, create a fake submenu just to add the list item image
			} else if ($menuItem.hasClass('menu-item--has-image')) {
				$menuItem.append(`<ul class="sub-menu faux">${listItemMarkup}</ul>`);
			}
		},
		/**
		 * Set a menu image's height allowance.
		 * This gets used for a pseudo element to make sure submenus
		 * don't spill out of their parent container.
		 *
		 * @param {JQuery} $subMenu
		 */
		setMenuImageAllowance: function($subMenu) {
			const menuHeight = $subMenu.outerHeight();
			const containerHeight = $subMenu.parents(this._opts.selectors.submenu).height();
			const $parentSubMenu = $subMenu.parents(this._opts.selectors.submenu);
			const allowance = menuHeight - containerHeight;
			$parentSubMenu.css('--menu-image-allowance', `${allowance > 0 ? Math.round(allowance) : 0}px`);
		},
		/**
		 * Add submenu headers so we can toggle the mobile menu "pages".
		 *
		 * @param {boolean} i Index
		 * @param {domElement} submenu The submenu we are adding the header to
		 */
		addSubMenuHeaders: function(i, submenu) {
			const $this = $(submenu);
			const text = $this.siblings('a').text();
			$this.prepend(`<li class="sub-menu-header">${text}</li>`);
		},
		/**
		 * Handle submenu header clicks.
		 * Basically, just closes the submenu.
		 * This is why we assembled all the menuToggles for easy access here!
		 *
		 * @param {domElement} subMenuHeader The submenu header element
		 */
		handleSubMenuHeaderClick: function(subMenuHeader) {
			const $this = $(subMenuHeader.currentTarget);
			const target = $this.parent().siblings(this._opts.selectors.submenuToggle).attr('id');
			this._data.menuToggles[target].close();
		},
		/**
		 * Observe all submenu elements so we can tel when DOM changes happen.
		 *
		 * @param {integer} i Index
		 * @param {domElement} submenu The submenu to observe
		 */
		observeSubMenus: function(i, submenu) {
			this._subMenuObserver.observe(submenu, {
				subtree: false,
				childList: false,
				attributes: true,
			});
		},
		/**
		 * Setup hover events on our menu items.
		 */
		setupMenuItemHoverEvents: function() {
			// Top level menu items hover
			$(`${this._opts.selectors.menu} > li`).on('mouseenter', e => {
				const $listItem = $(e.currentTarget);
				$listItem.find(`${this._opts.selectors.submenu} > .menu-item:nth-child(2)`).first().addClass('js-hover');
				this.setMenuImageAllowance($listItem.find(`${this._opts.selectors.submenu} ${this._opts.selectors.submenu}`));
			}).on('mouseleave', e => {
				$(e.currentTarget).find(`${this._opts.selectors.submenu} > .menu-item:nth-child(2)`).first().removeClass('js-hover');
			});

			// Sub menu item hover (.sub-menu .sub-menu)
			$(`${this._opts.selectors.menu} ${this._opts.selectors.submenu} > li`).on('mouseenter', e => {
				$(e.currentTarget).addClass('js-hover');
			}).on('mouseleave', e => {
				$(e.currentTarget).removeClass('js-hover').siblings().removeClass('js-hover');
			});
		}
	};

	// Self init
	module.init();
})();
